import { useEffect, useRef, useState } from "react";
import { useUser } from "@/context/UserContext";
import { getTaskState, startTask, verifyTask, friendlyApiError, isRateLimited } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Twitter, Repeat2, MessageCircle, Lock, Loader2, ExternalLink } from "lucide-react";

const PROFILE_URL = "https://x.com/OsteonNFT";

const TASKS = [
  { id: "follow", label: "Follow @OsteonNFT", icon: Twitter, url: PROFILE_URL },
  { id: "retweet", label: "Repost pinned post", icon: Repeat2, url: PROFILE_URL },
  { id: "comment", label: "Comment on pinned post", icon: MessageCircle, url: PROFILE_URL },
];

const ORDER = TASKS.map((t) => t.id);

export default function Tasks({ onOpenAuth }) {
  const { user } = useUser();
  // Source of truth is ALWAYS the backend (/api/tasks/state). Local state
  // only mirrors it — never the reverse. Keyed by account id so X-only
  // accounts reload correctly (a wallet-keyed reset was dropping progress).
  const [tasks, setTasks] = useState(null);
  const [busy, setBusy] = useState(null);
  // Bumped once per second while any countdown is active to re-render it.
  const [, setTick] = useState(0);
  const loadedAt = useRef(Date.now());
  const accountKey = user?.id ?? null;

  useEffect(() => {
    let active = true;
    if (!accountKey) {
      setTasks(null);
      return undefined;
    }
    getTaskState()
      .then((data) => {
        if (!active) return;
        loadedAt.current = Date.now();
        setTasks(data.tasks || []);
      })
      .catch(() => {
        if (active) setTasks([]);
      });
    return () => {
      active = false;
    };
  }, [accountKey]);

  const waiting = (tasks || []).some((t) => t.status === "started");
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [waiting]);

  // Countdown ticks locally for display only; the backend re-validates
  // the 5-second wait on every verify call regardless of local drift.
  const remaining = (task) => {
    if (task.status !== "started") return 0;
    const elapsed = Math.floor((Date.now() - loadedAt.current) / 1000);
    return Math.max(0, (task.verify_in_seconds ?? 5) - elapsed);
  };

  const applyState = (data) => {
    if (data && data.tasks) {
      loadedAt.current = Date.now();
      setTasks(data.tasks);
    }
  };

  const openTask = async (t) => {
    if (!user) {
      onOpenAuth();
      return;
    }
    // Never open the X tab for a task the server reports as locked (stale
    // UI, cross-tab race): the start call would fail after the tab opened.
    if ((byId[t.id]?.status) === "locked") {
      toast.error("Complete the previous task first.");
      try {
        applyState(await getTaskState());
      } catch {
        // Keep the last known state on refresh failure.
      }
      return;
    }
    // Open synchronously in the click handler so popup blockers allow it,
    // then record the start server-side (this begins the 5-second wait).
    window.open(t.url, "_blank", "noopener,noreferrer");
    setBusy(t.id);
    try {
      const data = await startTask(t.id);
      applyState(data);
      toast.success("Complete the action on X, then return here.");
    } catch (e) {
      if (isRateLimited(e)) {
        toast.error(friendlyApiError(e));
      } else if (e.response?.data?.detail === "TASK_LOCKED") {
        toast.error("Complete the previous task first.");
      } else {
        toast.error("Unable to start this task. Please try again.");
      }
      try {
        applyState(await getTaskState());
      } catch {
        // Keep the last known state on refresh failure.
      }
    } finally {
      setBusy(null);
    }
  };

  const verify = async (t) => {
    setBusy(t.id);
    try {
      const data = await verifyTask(t.id);
      applyState(data);
      toast.success("Task completed");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (isRateLimited(e)) {
        toast.error(friendlyApiError(e));
      } else if (detail === "TASK_LOCKED") {
        toast.error("Complete the previous task first.");
      } else if (detail.startsWith("TASK_TOO_EARLY")) {
        toast.error("Too soon — finish the countdown first.");
      } else if (detail === "TASK_NOT_STARTED") {
        toast.error("Open the task on X first.");
      } else {
        toast.error("Verification failed. Please try again.");
      }
      try {
        applyState(await getTaskState());
      } catch {
        // Keep the last known state on refresh failure.
      }
    } finally {
      setBusy(null);
    }
  };

  const byId = Object.fromEntries((tasks || []).map((t) => [t.id, t]));
  const completed = ORDER.filter((id) => byId[id]?.status === "completed").length;

  return (
    <section id="tasks" className="relative py-24 border-t border-[#1b2234]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono-cyber text-[11px] uppercase tracking-[0.35em] text-[#00f0ff] mb-4">// 02 — Social Tasks</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              Amplify the <span className="text-[#00f0ff]">signal</span>.
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              Three tasks, one fixed order. Each unlocks only after the previous one is done — progress is
              saved to your account and survives refresh. These are self-declared check-ins and don't affect
              points or the whitelist result.
            </p>
            <div className="mt-8 cyber-corners p-5 bg-[#0d1017]/70 border border-[#1b2234] clip-terminal">
              <div className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff]">Progress</div>
              <div className="mt-2 font-heading text-3xl font-bold">{completed} / {TASKS.length}</div>
              <div className="mt-3 h-1.5 bg-[#1b2234]">
                <div className="progress-fill h-full" style={{ width: `${(completed / TASKS.length) * 100}%` }} />
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {!user && (
              <div className="cyber-corners p-6 border border-[#1b2234] bg-[#0d1017]/70 clip-terminal">
                <Button
                  onClick={onOpenAuth}
                  data-testid="tasks-signin-btn"
                  className="rounded-none clip-notch bg-[#00f0ff] hover:bg-[#00f0ff]/90 text-black font-mono-cyber uppercase tracking-widest text-xs h-12 px-8"
                >
                  Sign In to Sync
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
            {user && tasks === null && (
              <div className="cyber-corners p-6 border border-[#1b2234] bg-[#0d1017]/70 clip-terminal font-mono-cyber text-xs uppercase tracking-widest text-slate-500">
                Loading tasks…
              </div>
            )}
            {user && tasks !== null && TASKS.map((t, index) => {
              const Icon = t.icon;
              const state = byId[t.id] || { status: "locked" };
              const done = state.status === "completed";
              const locked = state.status === "locked";
              const waitingNow = state.status === "started";
              const ready = state.status === "ready" || (waitingNow && remaining(state) <= 0);
              const secondsLeft = waitingNow ? remaining(state) : 0;
              const working = busy === t.id;
              return (
                <div
                  key={t.id}
                  data-testid={`task-${t.id}`}
                  className={`cyber-corners p-6 border ${
                    done ? "border-[#00ff66]/40 bg-[#00ff66]/5" : "border-[#1b2234] bg-[#0d1017]/70"
                  } clip-terminal`}
                >
                  <div className="flex items-center gap-5">
                    <div
                      className={`w-12 h-12 flex items-center justify-center border flex-shrink-0 ${
                        done ? "border-[#00ff66]/50 text-[#00ff66]" : "border-[#1b2234] text-slate-400"
                      }`}
                    >
                      {locked ? <Lock className="w-5 h-5" strokeWidth={1.5} /> : <Icon className="w-5 h-5" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-slate-500">
                        Task {index + 1} / {TASKS.length}
                      </div>
                      <div className="font-heading text-lg font-semibold uppercase tracking-wide mt-1">{t.label}</div>
                      {locked && (
                        <div className="font-mono-cyber text-[11px] uppercase tracking-widest text-slate-500 mt-1">
                          Locked — complete the previous task first.
                        </div>
                      )}
                      {done && (
                        <div data-testid={`task-status-${t.id}`} className="font-mono-cyber text-[11px] uppercase tracking-widest text-[#00ff66] mt-1">
                          Completed ✓
                        </div>
                      )}
                      {!done && !locked && (
                        <button
                          type="button"
                          onClick={() => openTask(t)}
                          data-testid={`task-open-${t.id}`}
                          className="font-mono-cyber text-[11px] uppercase tracking-widest text-[#00f0ff] hover:underline mt-1 inline-flex items-center gap-1"
                        >
                          Open on X <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {!done && !locked && state.status === "available" && (
                      <Button
                        onClick={() => openTask(t)}
                        disabled={working}
                        data-testid={`task-start-${t.id}`}
                        className="rounded-none clip-notch bg-[#00f0ff] hover:bg-[#00f0ff]/90 text-black font-mono-cyber uppercase tracking-widest text-[11px] h-11 px-6 flex-shrink-0"
                      >
                        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start"}
                      </Button>
                    )}
                    {waitingNow && secondsLeft > 0 && (
                      <div
                        data-testid={`task-status-${t.id}`}
                        className="font-mono-cyber text-[11px] uppercase tracking-widest text-[#ffb703] flex-shrink-0"
                      >
                        Verify in {secondsLeft}
                      </div>
                    )}
                    {ready && (
                      <Button
                        onClick={() => verify(t)}
                        disabled={working}
                        data-testid={`task-verify-${t.id}`}
                        className="rounded-none clip-notch bg-[#00ff66] hover:bg-[#00ff66]/90 text-black font-mono-cyber uppercase tracking-widest text-[11px] h-11 px-6 flex-shrink-0"
                      >
                        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify Completion"}
                      </Button>
                    )}
                    {done && <CheckCircle2 className="w-6 h-6 text-[#00ff66] flex-shrink-0" />}
                  </div>
                  {waitingNow && (
                    <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                      Complete the action on X, then return here. Self-declared check-in — we can't
                      confirm the X action happened.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
